import { z } from "zod";

/**
 * Quality Control (Sprint 2 Accreditation Bundle) — Westgard rules +
 * Levey-Jennings chart inputs. ISO 15189 §5.6 requires every accredited
 * lab to run QC daily and respond to rule violations.
 *
 * Pure functions in this module are reused by both the server (insert
 * path computes z-score + flags before writing) and the web (chart
 * tooltip can recompute z from current target + value for preview).
 */

// ===== Rule identifiers =====

export const WESTGARD_FLAGS = [
	"1_2s",
	"1_3s",
	"2_2s",
	"R_4s",
	"4_1s",
	"10x",
] as const;
export const westgardFlagSchema = z.enum(WESTGARD_FLAGS);
export type WestgardFlag = z.infer<typeof westgardFlagSchema>;

/**
 * Severity of each rule for UI styling:
 *   - 'warn'    — 1-2s warns but is non-rejecting by itself
 *   - 'reject'  — rule trip should reject the run
 *   - 'trend'   — long-term shift/drift; investigate calibration
 */
export const WESTGARD_SEVERITY: Record<
	WestgardFlag,
	"warn" | "reject" | "trend"
> = {
	"1_2s": "warn",
	"1_3s": "reject",
	"2_2s": "reject",
	R_4s: "reject",
	"4_1s": "trend",
	"10x": "trend",
};

/** Whether the run, taken together with the flags, would be marked rejected. */
export function isRejectingFlags(flags: WestgardFlag[]): boolean {
	return flags.some((f) => WESTGARD_SEVERITY[f] === "reject");
}

// ===== Target = expected mean + SD per (material, test) =====

export const qcTargetSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	material_id: z.string().uuid(),
	test_name: z.string(),
	expected_mean: z.number(),
	expected_sd: z.number().positive(),
	unit: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type QcTargetApi = z.infer<typeof qcTargetSchema>;

export const upsertQcTargetInputSchema = z.object({
	material_id: z.string().uuid(),
	test_name: z.string().min(1).max(120),
	expected_mean: z.number(),
	expected_sd: z.number().positive(),
	unit: z.string().max(50).nullable().optional(),
});
export type UpsertQcTargetInput = z.infer<typeof upsertQcTargetInputSchema>;

// ===== Material =====

export const qcMaterialSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	name: z.string(),
	level: z.string(),
	lot_number: z.string().nullable(),
	manufacturer: z.string().nullable(),
	expiry_date: z.string().nullable(),
	is_active: z.boolean(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
	/** Optional embedded targets — populated by GET /materials with ?withTargets. */
	targets: z.array(qcTargetSchema).optional(),
});
export type QcMaterialApi = z.infer<typeof qcMaterialSchema>;

export const createQcMaterialInputSchema = z.object({
	name: z.string().min(1).max(200),
	level: z.string().min(1).max(80),
	lot_number: z.string().max(100).nullable().optional(),
	manufacturer: z.string().max(200).nullable().optional(),
	expiry_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/u, "Use YYYY-MM-DD")
		.nullable()
		.optional(),
	notes: z.string().max(2000).nullable().optional(),
});
export type CreateQcMaterialInput = z.infer<typeof createQcMaterialInputSchema>;

export const updateQcMaterialInputSchema = createQcMaterialInputSchema
	.partial()
	.extend({ is_active: z.boolean().optional() });
export type UpdateQcMaterialInput = z.infer<typeof updateQcMaterialInputSchema>;

// ===== Runs =====

export const qcRunSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	material_id: z.string().uuid(),
	target_id: z.string().uuid().nullable(),
	test_name: z.string(),
	value: z.number(),
	unit: z.string().nullable(),
	z_score: z.number().nullable(),
	westgard_flags: z.array(westgardFlagSchema),
	accepted: z.boolean(),
	notes: z.string().nullable(),
	measured_at: z.string().datetime(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type QcRunApi = z.infer<typeof qcRunSchema>;

/** A single measurement in a batched recording. */
export const qcRunValueSchema = z.object({
	test_name: z.string().min(1).max(120),
	value: z.number(),
	unit: z.string().max(50).nullable().optional(),
	notes: z.string().max(500).nullable().optional(),
	/** Operator can mark a run as invalid up-front (e.g. clear reagent issue). */
	accepted: z.boolean().optional(),
});
export type QcRunValueInput = z.infer<typeof qcRunValueSchema>;

/**
 * Recording a daily QC pass: one material, many tests. The service
 * computes z-score + Westgard flags per row using recent history.
 */
export const recordQcRunsInputSchema = z.object({
	material_id: z.string().uuid(),
	measured_at: z.string().datetime().optional(),
	values: z.array(qcRunValueSchema).min(1).max(50),
});
export type RecordQcRunsInput = z.infer<typeof recordQcRunsInputSchema>;

export const qcChartQuerySchema = z.object({
	material_id: z.string().uuid(),
	test_name: z.string().min(1).max(120),
	limit: z.coerce.number().int().min(5).max(200).default(60),
});
export type QcChartQuery = z.infer<typeof qcChartQuerySchema>;

export const qcChartResponseSchema = z.object({
	target: qcTargetSchema.nullable(),
	runs: z.array(qcRunSchema),
});
export type QcChartResponse = z.infer<typeof qcChartResponseSchema>;

// ===== Westgard evaluator (pure) =====

/**
 * Compute Westgard rule flags + z-score for a single new run, given the
 * configured target and the recent run history (newest first, excluding
 * this run). Empty `recentRuns` is fine — only the single-point rules
 * (1-2s, 1-3s) can trigger then.
 *
 * Multi-point rules look at consecutive runs IN INSERTION ORDER (newest
 * first), which is what the chart query returns. The service is the
 * single writer for `westgard_flags`, so historical correctness depends
 * entirely on this function being deterministic for the same inputs.
 *
 * Sign convention: z = (value - mean) / sd. `sameSidePositive` = z > 0
 * for "above mean", `< 0` for "below mean".
 */
export interface WestgardInput {
	expected_mean: number;
	expected_sd: number;
}

export interface RecentRunForEval {
	/** The numeric measurement of the prior run. */
	value: number;
	/** Persisted z-score from when the prior run was inserted. */
	z_score: number | null;
	/** Excluded from rule application when false (operator-rejected). */
	accepted: boolean;
}

export interface WestgardOutcome {
	z_score: number;
	flags: WestgardFlag[];
}

export function evaluateWestgardRules(
	currentValue: number,
	target: WestgardInput,
	recentRuns: RecentRunForEval[],
): WestgardOutcome {
	const z = (currentValue - target.expected_mean) / target.expected_sd;
	const flags = new Set<WestgardFlag>();

	// Only consider accepted runs for multi-point rules — operator-rejected
	// runs would muddy the trend analysis.
	const history = recentRuns.filter((r) => r.accepted);
	const histZ = history.map((r) =>
		r.z_score != null
			? r.z_score
			: (r.value - target.expected_mean) / target.expected_sd,
	);

	const absZ = Math.abs(z);

	// 1-2s and 1-3s — single-point rules
	if (absZ > 2) flags.add("1_2s");
	if (absZ > 3) flags.add("1_3s");

	// 2-2s — two consecutive on the same side outside ±2 SD
	if (histZ.length >= 1) {
		const prev = histZ[0]!;
		if (absZ > 2 && Math.abs(prev) > 2 && Math.sign(z) === Math.sign(prev)) {
			flags.add("2_2s");
		}
		// R-4s — range across the two consecutive runs > 4 SD
		if (Math.abs(z - prev) > 4) {
			flags.add("R_4s");
		}
	}

	// 4-1s — four consecutive on same side outside ±1 SD
	if (histZ.length >= 3) {
		const window = [z, histZ[0]!, histZ[1]!, histZ[2]!];
		const allAbove = window.every((zi) => zi > 1);
		const allBelow = window.every((zi) => zi < -1);
		if (allAbove || allBelow) flags.add("4_1s");
	}

	// 10x — ten consecutive on same side of mean
	if (histZ.length >= 9) {
		const window = [z, ...histZ.slice(0, 9)];
		const allAbove = window.every((zi) => zi > 0);
		const allBelow = window.every((zi) => zi < 0);
		if (allAbove || allBelow) flags.add("10x");
	}

	return { z_score: z, flags: Array.from(flags) };
}
